import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { IItemCreationSubsectionInputProps } from '~/Components';
import { Clock } from '~/Components/Icons';
import { Schedule } from '~/Components/Icons/Schedule';
import { ItemCreationValidInput } from '~/Components/ItemCreation/ItemCreationSubsection/ItemCreationSubsectionInput.model';
import { AccessList, useDateFns, useHasAccess } from '~/Hooks';
import { EventType } from '~/Models/DTO/Event.model';
import { ExtendedEventSeries } from '../../../extendedEventSeries.model';

export const useTimeSeriesDate = ({
  timeSeries,
  eventType,
  hasAnyBookings,
  hasFutureBookings,
}: {
  timeSeries: ExtendedEventSeries;
  eventType: EventType;
  hasAnyBookings?: boolean;
  hasFutureBookings?: boolean;
}) => {
  const { t } = useTranslation();
  const { formatDate } = useDateFns();

  const today = formatDate(new Date().toISOString(), 'yyyy-MM-dd');
  const startDate = formatDate(timeSeries.startDate, 'yyyy-MM-dd');

  const { hasAccess } = useHasAccess();
  const canEditABookedSchedule = hasAccess(AccessList.EDIT_SCHEDULE_WITH_BOOKINGS);

  const timeSeriesDate = useMemo(() => {
    const inputs: {
      [key: string]: IItemCreationSubsectionInputProps<Partial<ExtendedEventSeries>>;
    } = {
      startDate: {
        icon: <Schedule />,
        label: t('time.startDate'),
        placeholder: t('time.startDate'),
        type: ItemCreationValidInput.DATE,
        name: 'startDate',
        InputProps: { inputProps: { disablePast: true } },
        disabled: hasAnyBookings,
      },
      startTime: {
        icon: <Clock />,
        label: t('time.startTime'),
        placeholder: t('time.startTime'),
        type: ItemCreationValidInput.TIME,
        name: 'dailyStartTime',
        disabled: hasFutureBookings && !canEditABookedSchedule,
        hasAnError: timeSeries.dailyStartTime > timeSeries.dailyEndTime,
      },
      endTime: {
        icon: <Clock />,
        label: t('time.endTime'),
        placeholder: t('time.endTime'),
        type: ItemCreationValidInput.TIME,
        name: 'dailyEndTime',
        InputProps: { inputProps: { min: new Date(timeSeries.dailyStartTime) } },
        hasAnError: timeSeries.dailyStartTime > timeSeries.dailyEndTime,
        disabled: hasFutureBookings && !canEditABookedSchedule,
      },
    };

    if (eventType === EventType.FREE_TRAINING) {
      return [inputs.startDate];
    }

    return [inputs.startDate, inputs.startTime, inputs.endTime];
  }, [
    eventType,
    startDate,
    today,
    timeSeries,
    hasAnyBookings,
    hasFutureBookings,
    canEditABookedSchedule,
  ]);

  return { timeSeriesDate };
};
